import { useEffect, useState } from 'react'
import { useAuth } from '../lib/auth'
import { User, Car, Key, Shield, LogOut, X, Copy, Check } from 'lucide-react'
import { Link } from 'react-router-dom'

export default function ResidentDashboard() {
  const { user, token, logout } = useAuth()
  const [showPinModal, setShowPinModal] = useState(false)
  const [pinCode, setPinCode] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const cards = [
    { title: 'My Profile', desc: 'View and update your personal information', icon: User, path: '/profile', color: 'bg-blue-500' },
    { title: 'My Vehicles', desc: 'Manage your registered vehicles', icon: Car, path: '/my-vehicles', color: 'bg-green-500' },
    { title: 'Access', desc: 'Your door access and PIN info', icon: Key, path: '/access', color: 'bg-purple-500' },
    { title: 'Security', desc: 'Change your password', icon: Shield, path: '/security', color: 'bg-orange-500' },
  ]

  useEffect(() => {
    const shouldShow = localStorage.getItem('show_pin_reminder') === '1'
    if (!shouldShow || !token) return

    const fetchPin = async () => {
      try {
        const res = await fetch('/api/auth/resident/profile', {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (res.ok) {
          const data = await res.json()
          setPinCode(data?.tenant?.pin_code || null)
        }
      } catch (err) {
        console.error('Failed to load PIN reminder data', err)
      } finally {
        setShowPinModal(true)
      }
    }

    fetchPin()
  }, [token])

  const closePinModal = () => {
    localStorage.removeItem('show_pin_reminder')
    setShowPinModal(false)
  }

  const copyPin = async () => {
    if (!pinCode) return
    try {
      await navigator.clipboard.writeText(pinCode)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy PIN', err)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {showPinModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md rounded-xl bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-gray-100 p-5">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Save Your Door PIN</h2>
                <p className="mt-1 text-sm text-gray-600">
                  Your account is ready. Please remember this PIN for door and gate access.
                </p>
              </div>
              <button
                onClick={closePinModal}
                className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                aria-label="Close PIN reminder"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-5">
              {pinCode ? (
                <>
                  <div className="rounded-lg border border-purple-200 bg-purple-50 p-4">
                    <p className="text-xs font-medium uppercase tracking-wide text-purple-700">Your PIN Code</p>
                    <p className="mt-1 text-center font-mono text-3xl font-bold tracking-widest text-purple-900">
                      {pinCode}
                    </p>
                  </div>
                  <p className="mt-3 text-sm text-gray-600">
                    Save this PIN now. You can also view it later in <span className="font-medium">Access</span>.
                  </p>
                  <div className="mt-4 flex gap-3">
                    <button
                      onClick={copyPin}
                      className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      {copied ? (
                        <span className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-green-600" />Copied</span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5"><Copy className="h-4 w-4" />Copy PIN</span>
                      )}
                    </button>
                    <button
                      onClick={closePinModal}
                      className="flex-1 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
                    >
                      I Saved It
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                    We could not load your PIN right now. Please open <span className="font-medium">Access</span> to view it.
                  </div>
                  <button
                    onClick={closePinModal}
                    className="mt-4 w-full rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
                  >
                    OK
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center">
              <span className="text-white font-bold text-lg">
                {user?.first_name?.[0] || 'R'}
              </span>
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">
                Welcome, {user?.first_name || 'Resident'}
              </h1>
              <p className="text-sm text-gray-500">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {cards.map((card) => {
            const Icon = card.icon
            return (
              <Link
                key={card.path}
                to={card.path}
                className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-6 flex items-start gap-4"
              >
                <div className={`${card.color} w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{card.title}</h3>
                  <p className="text-sm text-gray-500 mt-1">{card.desc}</p>
                </div>
              </Link>
            )
          })}
        </div>
      </main>
    </div>
  )
}
